<script setup lang="ts">
import { computed, ref, useAttrs, watch } from 'vue';
import { useTableColumns } from '../composables/useTableColumns';
import EmptyState from './EmptyState.vue';
import LoadingIndicator from './Loading/LoadingIndicator.vue';
import TableSkeleton from './Loading/TableSkeleton.vue';

defineOptions({ inheritAttrs: false });

const props = withDefaults(
  defineProps<{
    loading?: boolean;
    items?: unknown[];
  }>(),
  { loading: undefined, items: undefined },
);

const attrs = useAttrs();
const containerRef = ref<HTMLElement | null>(null);
const tableRef = ref<any>(null);
const requestLoading = ref<boolean>(false);
const hasLoaded = ref<boolean>(false);

const isManaged = computed(() => props.loading !== undefined || Boolean(attrs.url));
const loading = computed(() => Boolean(props.loading) || requestLoading.value);
const isInitialLoading = computed(() => isManaged.value && !hasLoaded.value);
const isRefreshing = computed(() => hasLoaded.value && loading.value);
const isEmpty = computed(() => hasLoaded.value && props.items?.length === 0);

const { columns } = useTableColumns(containerRef);

watch(loading, (current, previous) => {
  if (previous && !current) hasLoaded.value = true;
});

watch(
  () => props.items,
  () => {
    if (!loading.value) hasLoaded.value = true;
  },
);

defineExpose({
  applyLookFor: (...args: any[]) => tableRef.value?.applyLookFor(...args),
});
</script>

<template>
  <div
    ref="containerRef"
    class="data-table"
    :class="{
      'data-table--initial-loading': isInitialLoading,
      'data-table--refreshing': isRefreshing,
      'data-table--empty': isEmpty,
    }"
    :aria-busy="isInitialLoading || isRefreshing"
  >
    <div class="data-table__content" :inert="isRefreshing">
      <Table
        ref="tableRef"
        v-bind="$attrs"
        :items="items"
        :busy="isInitialLoading"
        @loading="requestLoading = $event"
      >
        <template v-for="(_, name) in $slots" #[name]="scope">
          <slot :name="name" v-bind="scope ?? {}" />
        </template>
        <template #table-busy>
          <TableSkeleton :columns="columns" />
        </template>
      </Table>
      <EmptyState v-if="isEmpty" />
    </div>
    <Transition name="data-table-fade">
      <div v-if="isRefreshing" class="data-table__overlay">
        <LoadingIndicator />
      </div>
    </Transition>
  </div>
</template>

<style scoped lang="scss">
.data-table {
  position: relative;

  :deep(.b-table-busy) {
    tbody > tr:not(.b-table-busy-slot) {
      display: none;
    }

    .b-table-busy-slot {
      position: static;
      display: table-row;
      width: auto;
      height: auto;
      background: transparent;
    }

    .b-table-busy-slot > td {
      padding: 0;
      border-bottom: 0;
      background: transparent;
      box-shadow: none;
    }
  }

  &--initial-loading :deep(.pagination) {
    visibility: hidden;
  }

  &--refreshing {
    min-height: 16rem;
  }

  &--empty {
    :deep(.b-table-empty-slot),
    :deep(.pagination) {
      display: none;
    }
  }

  &__overlay {
    position: absolute;
    inset: 0;
    z-index: 6;
    display: flex;
    justify-content: center;
    align-items: flex-start;
    padding-top: 3.5rem;
    border-radius: 0.75rem;
    background: rgba(255, 255, 255, 0.55);
    backdrop-filter: blur(3px);
  }
}

.data-table-fade-enter-active,
.data-table-fade-leave-active {
  transition: opacity 0.2s ease;
}

.data-table-fade-enter-from,
.data-table-fade-leave-to {
  opacity: 0;
}
</style>
